import { LabeledValueWithFieldInfo } from "@/types/user"
import { LabeledValue } from "@/types/labeledValue"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { CaseDetails, TemplateDetails } from "@/types"
import { LabelFormat } from "@/types/insights"
import { padStart } from "lodash"
import moment from "moment"
import { Component, Vue } from "vue-property-decorator"

// TODO - TechDebt This should have been written using class syntax if it was intended to be used as a mixin properly
// TODO - Remove this version and replace existing usages with class version bellow
export const formatFieldsMixin = {
	methods: {
		/**
		 * Formats the value of the field as a string depending on the data type
		 * @param dataType  Data type of the field
		 * @param fieldValue The fields value
		 * @param dataSourceItems Items from a datasource that is used by the field
		 */
		formatFieldValue (dataType: string, fieldValue: any, dataSourceItems: any[] = []) {
			// Format custom field values based of dataType
			switch (dataType) {
				case "date":
					return formatDateMixin.methods.displayDateOnlyFromISOString(fieldValue)
				case "date-time":
					return formatDateMixin.methods.displayDateFromISOString(fieldValue)
				case "dollar":
					return "$" + fieldValue
				case "checkbox":
					return fieldValue ? "Yes" : "No"
				case "list":
					const option = dataSourceItems ? dataSourceItems.find((s: any) => s.id === fieldValue) : null
					return option ? option.label : ""
				case "list-multiple":
					const values: string[] = fieldValue.map((v: any) => v.label)
					return values.toString()
				default:
					return fieldValue
			}
		},

		formatLabel (format: LabelFormat, label: string): string {
			switch (format) {
				case LabelFormat.Date:
					if (!label || label === "Not Set") return "Not set"

					const momentDate = moment(label)
					return momentDate.isValid() ? momentDate.format("L") : label
				case LabelFormat.WeekDate:
					if (!label || label === "Not Set") return "Not set"

					const momentWeek = moment(label)
					return momentWeek.isValid() ? `Week of ${momentWeek.format("L")}` : label
				case LabelFormat.WeekRange:
					if (!label || label === "Not Set") return "Not set"

					return "Week " + this.weekNumber(new Date(label)) // Do not use moment's ISO Weeks as it incorrectly uses week start
				case LabelFormat.MonthDate:
					if (!label || label === "Not Set") return "Not set"

					const momentMonth = moment(label)
					return momentMonth.isValid() ? `${momentMonth.format("MMMM")} ${momentMonth.format("YYYY")}` : label
				default:
					return label === null || label === undefined || label === "" ? "None" : label
			}
		},

		getDisplayLabelFromLabeledValue (labeledValue: LabeledValue, dataType: string): string {
			if (labeledValue.label !== undefined && labeledValue.label !== null) {
				return labeledValue.label
			}

			if (dataType === "checkbox") {
				if (labeledValue.value) {
					return "Yes"
				} else {
					return "No"
				}
			} else if (dataType === "map-path") {
				if (labeledValue.value) {
					return "Map Path"
				}
				return ""
			} else if (dataType === "map-marker") {
				if (labeledValue.value) {
					return "Map Marker"
				}
				return ""
			} else if (dataType === "map-region") {
				if (labeledValue.value) {
					return "Map Region"
				}
				return ""
			} else if (dataType === "list-multiple") {
				if (labeledValue.value) {
					return labeledValue.value.map((c: any) => c.label).join(", ")
				}
				return ""
			} else if (dataType === "list-data") {
				if (labeledValue.value) {
					return labeledValue.value.map((c: any) => c.label).join(", ")
				}
				return ""
			} else if (dataType === "date-time") {
				return formatDateMixin.methods.displayDateFromISOString(labeledValue.value)
			} else if (dataType === "date") {
				return formatDateMixin.methods.displayDateOnlyFromISOString(labeledValue.value)
			} else if (labeledValue.value !== undefined && labeledValue.value != null) {
				return labeledValue.value
			} else {
				return ""
			}
		},

		getDisplayLabelFromLabeledValueWithFieldInfo (labeledValue: LabeledValueWithFieldInfo): string {
			const dataType = labeledValue.dataType
			return this.getDisplayLabelFromLabeledValue(labeledValue as LabeledValue, dataType)
		},

		weekNumber (weekStart: Date): number {
			const firstDayOfWeek = moment.localeData().firstDayOfWeek()
			const jan4 = new Date(weekStart.getFullYear(), 1, 4)

			const correction = jan4.getDay() + 3
			const dayOfYear = this.dayOfYear(weekStart)
			const weekNumber = Math.floor((dayOfYear + correction - firstDayOfWeek) / 7)

			return weekNumber <= 0 ? 53 : weekNumber
		},

		dayOfYear (date: Date): number {
			const start = new Date(date.getFullYear(), 0, 0)

			// Plus before the date gets around "The right-hand side of an arithmetic operation must be of type ... " error
			const diff = (+date - +start) + ((start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000)
			const oneDay = 1000 * 60 * 60 * 24
			return Math.floor(diff / oneDay)
		},

		formatWeekStart (weekStart: Date): string {
			const momentDate = moment(weekStart)
			if (momentDate.isValid()) {
				return momentDate.format("L")
			}
			return ""
		},

		formatWeekEnd (weekStart: Date): string {
			const momentDate = moment(weekStart).add(6, "days")
			if (momentDate.isValid()) {
				return momentDate.format("L")
			}
			return ""
		},

		showField (template: TemplateDetails, fieldKey: string): boolean {
			if (!template) return false
			if (!template.hiddenFields) return true
			return !template.hiddenFields!.includes(fieldKey)
		},

		timeInHoursAndMinutes (timeValue: number | null | undefined) {
			if (timeValue === null || timeValue === undefined) return ""
			const hours = Math.trunc(timeValue)
			const minutes = Math.round((timeValue % 1) * 60)

			return hours.toString() + ":" + padStart(minutes.toString(), 2, "0")
		},
		/**
		 * Get field Label value for field ID
		 */
		getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
			if (!caseDetails.fields || !caseDetails.fields[fieldId]) {
				return ""
			}

			if (Array.isArray(caseDetails.fields[fieldId])) {
				return caseDetails.fields[fieldId].value.map((i: { label: string }) => i.label).join(", ")
			}

			return caseDetails.fields[fieldId].label
		},

		listFieldDisplayValue (column: any, caseItem: any): string | null {
			if (caseItem[column.key] && caseItem[column.key].label) {
				return caseItem[column.key].label
			} else {
				return column.displayKey
					? caseItem.case[column.displayKey]
					: column.fieldId
						? this.getFieldLabel(column.fieldId, caseItem.case)
						: caseItem[column.key].value && Array.isArray(caseItem[column.key].value)
							? caseItem[column.key].value.map((i:any) => i[column.listLabel || "label"]).join(", ")
							: caseItem[column.key].value
			}
		}
	}
}

/**
 * Wrapper to allow the formatFieldsMixin to be used with the extends Mixin() decorator
 */
@Component({
})
export class FormatFieldsMixin extends Vue {
	/**
	 * Formats the value of the field as a string depending on the data type
	 * @param dataType  Data type of the field
	 * @param fieldValue The fields value
	 * @param dataSourceItems Items from a datasource that is used by the field
	 */
	formatFieldValue (dataType: string, fieldValue: any, dataSourceItems: any[] = []) {
		return formatFieldsMixin.methods.formatFieldValue(dataType, fieldValue, dataSourceItems)
	}

	formatLabel (format: LabelFormat, label: string) {
		return formatFieldsMixin.methods.formatLabel(format, label)
	}

	getDisplayLabelFromLabeledValue (labeledValue: LabeledValue, dataType: string) {
		return formatFieldsMixin.methods.getDisplayLabelFromLabeledValue(labeledValue, dataType)
	}

	getDisplayLabelFromLabeledValueWithFieldInfo (labeledValue: LabeledValueWithFieldInfo) {
		return formatFieldsMixin.methods.getDisplayLabelFromLabeledValueWithFieldInfo(labeledValue)
	}

	weekNumber (weekStart: Date) {
		return formatFieldsMixin.methods.weekNumber(weekStart)
	}

	dayOfYear (date: Date) {
		return formatFieldsMixin.methods.dayOfYear(date)
	}

	formatWeekStart (weekStart: Date) {
		return formatFieldsMixin.methods.formatWeekStart(weekStart)
	}

	formatWeekEnd (weekStart: Date) {
		return formatFieldsMixin.methods.formatWeekEnd(weekStart)
	}

	showField (template: TemplateDetails, fieldKey: string) {
		return formatFieldsMixin.methods.showField(template, fieldKey)
	}

	timeInHoursAndMinutes (timeValue: number | null | undefined) {
		return formatFieldsMixin.methods.timeInHoursAndMinutes(timeValue)
	}
}
